import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders, isSuperuser } from '../api/auth';
import PageContainer from '../components/PageContainer';

const API_BASE = '/api';

interface SessionRow {
  id: number;
  session_id: string;
  domain_id: number | null;
  domain_name: string | null;
  language: string | null;
  client_ip: string | null;
  first_message_at: string | null;
  last_message_at: string | null;
  message_count: number;
  first_message_preview: string;
}

interface Citation {
  title?: string;
  document_id?: number;
  chunk_id?: number;
  source_url?: string;
  snippet?: string;
}

interface MessageRow {
  turn_index: number;
  user_text: string;
  bot_text: string;
  intent: string | null;
  intent_confidence: number | null;
  confidence: number | null;
  finish_reason: string | null;
  citations: Citation[];
  tool_calls_made: string[];
  tokens_used: number | null;
  duration_ms: number | null;
  created_at: string;
}

interface SessionDetail extends SessionRow {
  user_agent: string | null;
  messages: MessageRow[];
}

interface DomainOpt { id: number; name_en: string; }

const Chats: React.FC = () => {
  const [items, setItems] = useState<SessionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [domainId, setDomainId] = useState<number | null>(null);
  const [lang, setLang] = useState<string>('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [domains, setDomains] = useState<DomainOpt[]>([]);
  const [openSession, setOpenSession] = useState<SessionDetail | null>(null);
  const [openLoading, setOpenLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params: any = { page, page_size: pageSize };
      if (domainId !== null) params.domain_id = domainId;
      if (lang) params.language = lang;
      if (q) params.q = q;
      const resp = await axios.get(`${API_BASE}/admin/chats`, {
        headers: getAuthHeaders(),
        params,
      });
      setItems(resp.data.items);
      setTotal(resp.data.total);
    } catch (e: any) {
      setErr(e.response?.data?.detail || 'Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, domainId, lang, q]);

  useEffect(() => { load(); }, [load]);

  // Pull domain list once for the filter dropdown
  useEffect(() => {
    axios.get(`${API_BASE}/admin/data/domains?page_size=200`, { headers: getAuthHeaders() })
      .then(r => setDomains((r.data.items || []).map((d: any) => ({ id: d.id, name_en: d.name_en }))))
      .catch(() => {});
  }, []);

  const openTranscript = async (s: SessionRow) => {
    setOpenLoading(true);
    try {
      const resp = await axios.get(`${API_BASE}/admin/chats/${s.session_id}`, { headers: getAuthHeaders() });
      setOpenSession(resp.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to open transcript');
    } finally {
      setOpenLoading(false);
    }
  };

  if (!isSuperuser()) {
    return (
      <PageContainer title="Chat history">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
          Superuser privileges required to view chat history.
        </div>
      </PageContainer>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <PageContainer
      title="Chat history"
      subtitle="Every conversation the bot has handled, newest first. Click a row to see the full transcript with citations, tool calls, intent, and end-to-end latency for each turn."
    >

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="eyebrow block mb-1">Search</label>
          <input
            className="input-base text-sm"
            placeholder="search user / bot text…"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(); } }}
          />
        </div>
        <div>
          <label className="eyebrow block mb-1">Domain</label>
          <select className="input-base text-sm" value={domainId === null ? '' : domainId}
                  onChange={e => { setPage(1); setDomainId(e.target.value === '' ? null : Number(e.target.value)); }}>
            <option value="">All</option>
            <option value={0}>(no domain)</option>
            {domains.map(d => <option key={d.id} value={d.id}>{d.name_en}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Language</label>
          <select className="input-base text-sm" value={lang}
                  onChange={e => { setPage(1); setLang(e.target.value); }}>
            <option value="">Any</option>
            <option value="en">English</option>
            <option value="ar">Arabic</option>
          </select>
        </div>
        <button onClick={() => { setPage(1); load(); }} className="btn-primary">Apply</button>
        <button onClick={() => { setQ(''); setLang(''); setDomainId(null); setPage(1); }} className="text-sm text-primary-500 hover:text-primary-700">Reset</button>
        <div className="ml-auto text-xs text-primary-500">{total.toLocaleString()} session(s)</div>
      </div>

      {err && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm">{err}</div>
      )}

      <div className="bg-white border border-primary-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-primary-50 text-primary-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Last message</th>
              <th className="text-left px-4 py-3 font-semibold">First user message</th>
              <th className="text-left px-4 py-3 font-semibold">Domain</th>
              <th className="text-left px-4 py-3 font-semibold">Lang</th>
              <th className="text-left px-4 py-3 font-semibold">Turns</th>
              <th className="text-left px-4 py-3 font-semibold">Session ID</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-primary-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-primary-400">No sessions match the filters.</td></tr>
            )}
            {items.map(s => (
              <tr key={s.id} className="border-t border-primary-100 hover:bg-primary-50 cursor-pointer"
                  onClick={() => openTranscript(s)}>
                <td className="px-4 py-3 text-primary-700 whitespace-nowrap">
                  {s.last_message_at ? new Date(s.last_message_at).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-primary-900 max-w-md truncate" title={s.first_message_preview}>
                  {s.first_message_preview || <span className="text-primary-400 italic">(empty)</span>}
                </td>
                <td className="px-4 py-3 text-primary-700">
                  {s.domain_name || <span className="text-primary-400 italic">—</span>}
                </td>
                <td className="px-4 py-3 text-primary-600 text-xs uppercase">{s.language || '—'}</td>
                <td className="px-4 py-3 text-primary-700">{s.message_count}</td>
                <td className="px-4 py-3 text-primary-500 font-mono text-xs truncate max-w-[180px]" title={s.session_id}>
                  {s.session_id.slice(0, 8)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-primary-100 bg-primary-50 text-sm">
            <span className="text-primary-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-primary-300 text-primary-700 disabled:opacity-40">Prev</button>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-primary-300 text-primary-700 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {openSession && (
        <TranscriptModal session={openSession} onClose={() => setOpenSession(null)} />
      )}
      {openLoading && !openSession && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center text-white text-sm">Loading transcript…</div>
      )}
    </PageContainer>
  );
};

// ---------- Transcript modal ----------
interface TranscriptModalProps {
  session: SessionDetail;
  onClose: () => void;
}

const TranscriptModal: React.FC<TranscriptModalProps> = ({ session, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-primary-200 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="eyebrow mb-1">Transcript</div>
            <div className="text-sm text-primary-700 truncate">
              <span className="font-mono text-xs text-primary-500">{session.session_id}</span>
              {session.domain_name && (
                <span className="ml-3 px-2 py-0.5 rounded bg-primary-100 text-primary-700 text-xs">{session.domain_name}</span>
              )}
              {session.language && (
                <span className="ml-2 text-xs text-primary-500 uppercase">{session.language}</span>
              )}
            </div>
            <div className="text-xs text-primary-500 mt-1">
              {session.message_count} turn(s) · started {session.first_message_at && new Date(session.first_message_at).toLocaleString()}
              {session.client_ip && <span className="ml-2">· {session.client_ip}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-primary-400 hover:text-primary-700 text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {session.messages.map(m => (
            <div key={m.turn_index} className="space-y-3">
              {/* User bubble */}
              <div className="flex justify-end">
                <div className="max-w-[80%] bg-primary/10 text-primary-900 rounded-2xl rounded-br-md px-4 py-3 text-sm whitespace-pre-wrap"
                     dir={session.language === 'ar' ? 'rtl' : 'ltr'}>
                  {m.user_text || <span className="text-primary-400 italic">(empty)</span>}
                </div>
              </div>
              {/* Bot bubble */}
              <div className="flex">
                <div className="max-w-[80%] bg-primary-50 border border-primary-200 text-primary-900 rounded-2xl rounded-bl-md px-4 py-3 text-sm whitespace-pre-wrap"
                     dir={session.language === 'ar' ? 'rtl' : 'ltr'}>
                  {m.bot_text || <span className="text-primary-400 italic">(empty)</span>}
                  {m.citations && m.citations.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-primary-200 space-y-1.5">
                      {m.citations.map((c, i) => (
                        <div key={i} className="text-xs text-primary-600">
                          <span className="font-medium text-primary-700">[{i + 1}]</span>{' '}
                          {c.title || `Document ${c.document_id}`}
                          {c.source_url && (
                            <a href={c.source_url} target="_blank" rel="noreferrer" className="ml-2 text-primary-700 hover:underline">source ↗</a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-primary-500">
                    {m.intent && <span>intent: <span className="font-medium text-primary-700">{m.intent}</span> ({Math.round((m.intent_confidence || 0) * 100)}%)</span>}
                    {m.tool_calls_made && m.tool_calls_made.length > 0 && (
                      <span>tools: <span className="font-medium text-primary-700">{m.tool_calls_made.join(', ')}</span></span>
                    )}
                    {m.duration_ms !== null && m.duration_ms !== undefined && (
                      <span>· {m.duration_ms}ms</span>
                    )}
                    {m.tokens_used ? <span>· {m.tokens_used} tok</span> : null}
                    {m.finish_reason && m.finish_reason !== 'stop' && (
                      <span className="text-amber-700">· {m.finish_reason}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Chats;
